

import { selectAuthoritativeCanvasTabs } from './artifactBindingProjection.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
/**
 * DOC HELPER — Canvas tab shell selector boundary.
 * This selector decides only which canonical tabs are visible from backend
 * state presence. Per-tab toolbar actions are externally supplied capability
 * payloads and are never inferred here from tab keys or business modes.
 */

function isSourcingEvidence(row) {
  const kind = String(firstTruthy(() => (Object(row).evidence_kind), () => (''))).trim();
  const role = String(firstTruthy(() => (Object(row).result_role), () => (Object(row).sourcing_result_role), () => (''))).trim().toLowerCase();
  if (kind !== 'sourcing') {
    return false;
  }
  if (role === 'candidate') {
    return true;
  }
  if (role === 'reference') {
    return false;
  }
  return Boolean(String(firstTruthy(() => (Object(row).supplier_id), () => (Object(row).supplier_name), () => (Object(row).vendor_name), () => (''))).trim());
}

export function selectCanvasEvidencePresence(canvasEvidenceItems = []) {
  const evidenceRows = Array.isArray(canvasEvidenceItems) ? canvasEvidenceItems : [];
  return {
    hasSourcingEvidenceRows: evidenceRows.some((row) => isSourcingEvidence(row)),
    hasRetrievalEvidenceRows: evidenceRows.some((row) => !isSourcingEvidence(row)),
  };
}

export function selectCanvasTabs({ artifactTabProjection } = {}) {
  return selectAuthoritativeCanvasTabs(artifactTabProjection);
}

export function selectResolvedCanvasEvidenceTabKey({
  hasSourcingCanvasState,
  hasSourcingEvidenceRows,
  hasRetrievalEvidenceRows,
  canvasActiveTabKey,
} = {}) {
  const hasSourcingWorkspace = Boolean(hasSourcingEvidenceRows || (hasSourcingCanvasState && !hasRetrievalEvidenceRows));
  return hasSourcingWorkspace && canvasActiveTabKey === 'sourcing'
    ? 'sourcing'
    : 'retrieval';
}

export function selectResolvedCanvasActiveTabKey(options) {
  const { canvasActiveTabKey, canvasTabs, artifactTabProjection } = { canvasActiveTabKey: '', canvasTabs: [], artifactTabProjection: null, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const requestedKey = String(canvasActiveTabKey || '').trim();
  const tabs = Array.isArray(canvasTabs) ? canvasTabs : [];
  const matchedTab = requestedKey
    ? tabs.find((tab) => String(tab?.key || '').trim() === requestedKey)
    : null;
  const canonicalTab = tabs.find((tab) => tab.key === artifactTabProjection?.active_tab);
  return String((matchedTab || canonicalTab || {})?.key || '').trim();
}

/** Local selection may choose among canonical tabs, never infer workflow tabs. */
export function selectCanvasAutoTab({ canvasTabs = [], artifactTabProjection, showCanvasPanel, canvasActiveTabKey = '' } = {}) {
  if (!showCanvasPanel) return '';
  const resolvedActiveTabKey = selectResolvedCanvasActiveTabKey({ canvasActiveTabKey, canvasTabs, artifactTabProjection });
  return resolvedActiveTabKey === String(canvasActiveTabKey).trim() ? '' : resolvedActiveTabKey;
}

export function selectShouldAutoOpenCanvasForSourcing({
  hasSourcingEvidenceRows,
  hadSourcingEvidence,
} = {}) {
  return Boolean(hasSourcingEvidenceRows && !hadSourcingEvidence);
}
